import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./TestingDetails.css";

function TestingDetails() {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // View mode: 'list' or 'form'
  const [viewMode, setViewMode] = useState("list");
  const [editingId, setEditingId] = useState(null);

  // Filter states
  const [filterSize, setFilterSize] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterStandard, setFilterStandard] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    sizes: [],
    classes: [],
    testingStandards: []
  });

  // Dropdown options for the form
  const [dropdownOptions, setDropdownOptions] = useState({
    sizes: [],
    classes: [],
    testingStandards: []
  });

  // Form data
  const [formData, setFormData] = useState({
    size: "",
    class: "",
    testing_standard: "",
    hydro_back_seat_pressure: "",
    hydro_back_seat_duration: "",
    hydro_back_seat_acceptance: "",
    hydro_body_pressure: "",
    hydro_body_duration: "",
    hydro_body_acceptance: "",
    hydro_seat_pressure: "",
    hydro_seat_duration: "",
    hydro_seat_acceptance: "",
    pneumatic_seat_pressure: "",
    pneumatic_seat_duration: "",
    pneumatic_seat_acceptance: ""
  });

  // Fetch mappings
  const fetchMappings = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterSize) params.size = filterSize;
      if (filterClass) params.class = filterClass;
      if (filterStandard) params.testing_standard = filterStandard;

      const response = await axios.get("/testing-parameters", { params });
      setMappings(response.data.data || []);
    } catch (err) {
      setError("Failed to fetch testing parameters");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dropdown options for form
  const fetchDropdownOptions = async () => {
    try {
      const response = await axios.get("/testing-parameters/options");
      setDropdownOptions(response.data.data || { sizes: [], classes: [], testingStandards: [] });
    } catch (err) {
      console.error("Failed to fetch dropdown options:", err);
    }
  };

  // Fetch filter options from existing mapping
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get("/testing-parameters/filter-options");
      setFilterOptions(response.data.data || { sizes: [], classes: [], testingStandards: [] });
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
    }
  };

  useEffect(() => {
    fetchMappings();
    fetchDropdownOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [filterSize, filterClass, filterStandard]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      size: "",
      class: "",
      testing_standard: "",
      hydro_back_seat_pressure: "",
      hydro_back_seat_duration: "",
      hydro_back_seat_acceptance: "",
      hydro_body_pressure: "",
      hydro_body_duration: "",
      hydro_body_acceptance: "",
      hydro_seat_pressure: "",
      hydro_seat_duration: "",
      hydro_seat_acceptance: "",
      pneumatic_seat_pressure: "",
      pneumatic_seat_duration: "",
      pneumatic_seat_acceptance: ""
    });
    setEditingId(null);
  };

  // Open form for new mapping
  const handleAddNew = () => {
    resetForm();
    setViewMode("form");
  };

  // Open form for editing
  const handleEdit = async (mapping) => {
    setFormData({
      size: mapping.size || "",
      class: mapping.class || "",
      testing_standard: mapping.testing_standard || "",
      hydro_back_seat_pressure: mapping.hydro_back_seat_pressure || "",
      hydro_back_seat_duration: mapping.hydro_back_seat_duration || "",
      hydro_back_seat_acceptance: mapping.hydro_back_seat_acceptance || "",
      hydro_body_pressure: mapping.hydro_body_pressure || "",
      hydro_body_duration: mapping.hydro_body_duration || "",
      hydro_body_acceptance: mapping.hydro_body_acceptance || "",
      hydro_seat_pressure: mapping.hydro_seat_pressure || "",
      hydro_seat_duration: mapping.hydro_seat_duration || "",
      hydro_seat_acceptance: mapping.hydro_seat_acceptance || "",
      pneumatic_seat_pressure: mapping.pneumatic_seat_pressure || "",
      pneumatic_seat_duration: mapping.pneumatic_seat_duration || "",
      pneumatic_seat_acceptance: mapping.pneumatic_seat_acceptance || ""
    });
    setEditingId(mapping.id);
    setViewMode("form");
  };

  // Back to list
  const handleBackToList = () => {
    resetForm();
    setViewMode("list");
    setError(null);
    setSuccess(null);
  };

  // Handle form submission
  const handleSubmit = async (e, addAnother = false) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate required fields
    if (!formData.size || !formData.class || !formData.testing_standard) {
      setError("Size, Class, and Testing Standard are required fields");
      return;
    }

    // Validate hydrostatic body
    if (!formData.hydro_body_pressure || !formData.hydro_body_duration || !formData.hydro_body_acceptance) {
      setError("All Hydrostatic Body fields are required");
      return;
    }

    // Validate hydrostatic seat
    if (!formData.hydro_seat_pressure || !formData.hydro_seat_duration || !formData.hydro_seat_acceptance) {
      setError("All Hydrostatic Seat fields are required");
      return;
    }

    // Validate pneumatic seat
    if (!formData.pneumatic_seat_pressure || !formData.pneumatic_seat_duration || !formData.pneumatic_seat_acceptance) {
      setError("All Pneumatic Seat fields are required");
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        await axios.put(`/testing-parameters/${editingId}`, formData);
        setSuccess("Testing parameter mapping updated successfully");
        if (!addAnother) {
          setTimeout(() => {
            handleBackToList();
            fetchMappings();
            fetchFilterOptions();
          }, 1000);
        }
      } else {
        await axios.post("/testing-parameters", formData);
        setSuccess("Testing parameter mapping created successfully");

        if (addAnother) {
          resetForm();
          fetchMappings();
          fetchFilterOptions();
        } else {
          setTimeout(() => {
            handleBackToList();
            fetchMappings();
            fetchFilterOptions();
          }, 1000);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save testing parameter");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this mapping?")) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`/testing-parameters/${id}`);
      setSuccess("Testing parameter mapping deleted successfully");
      fetchMappings();
      fetchFilterOptions();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete testing parameter");
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilterSize("");
    setFilterClass("");
    setFilterStandard("");
  };

  // Render List View
  const renderListView = () => (
    <>
      {/* Header */}
      <div className="testing-details-header">
        <button className="back-button" onClick={() => navigate("/admin")}>
          &larr; Back to Admin Panel
        </button>
        <div className="header-content">
          <h1>Testing Details Master</h1>
          <p>Manage testing parameters mapping for Size, Class, and Standard combinations</p>
        </div>
        <button className="add-mapping-btn" onClick={handleAddNew}>
          + Add New Mapping
        </button>
      </div>

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filter Mappings</h3>
        <div className="filters-row">
          <div className="filter-group">
            <label>Size</label>
            <select value={filterSize} onChange={(e) => setFilterSize(e.target.value)}>
              <option value="">All Sizes</option>
              {filterOptions.sizes.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Class</label>
            <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
              <option value="">All Classes</option>
              {filterOptions.classes.map((cls) => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Testing Standard</label>
            <select value={filterStandard} onChange={(e) => setFilterStandard(e.target.value)}>
              <option value="">All Standards</option>
              {filterOptions.testingStandards.map((std) => (
                <option key={std} value={std}>{std}</option>
              ))}
            </select>
          </div>
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Mappings Table */}
      <div className="mappings-table-container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : mappings.length === 0 ? (
          <div className="empty-state">
            <p>No testing parameter mappings found</p>
            <button onClick={handleAddNew}>Create First Mapping</button>
          </div>
        ) : (
          <table className="mappings-table">
            <thead>
              <tr>
                <th>Size</th>
                <th>Class</th>
                <th>Testing Standard</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((mapping) => (
                <tr key={mapping.id}>
                  <td>{mapping.size}</td>
                  <td>{mapping.class}</td>
                  <td>{mapping.testing_standard}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(mapping)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(mapping.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  // Render Form View
  const renderFormView = () => (
    <div className="form-page">
      {/* Back to List Link */}
      <button className="back-to-list-link" onClick={handleBackToList}>
        &larr; Back to List
      </button>

      <div className="form-page-header">
        <h1>{editingId ? "Edit Mapping" : "Add New Mapping"}</h1>
        <p>Configure test parameters for MTC auto-population</p>
      </div>

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={(e) => handleSubmit(e, false)}>
        <div className="form-card">
          {/* Identification Section */}
          <div className="form-section">
            <h3>Identification (Composite Key)</h3>
            <div className="form-row two-columns">
              <div className="form-group">
                <label>Size <span className="required">*</span></label>
                <select
                  name="size"
                  value={formData.size}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Size</option>
                  {dropdownOptions.sizes.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Class <span className="required">*</span></label>
                <select
                  name="class"
                  value={formData.class}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Class</option>
                  {dropdownOptions.classes.map((cls) => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group full-width">
              <label>Testing Standard & Edition <span className="required">*</span></label>
              <select
                name="testing_standard"
                value={formData.testing_standard}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Standard</option>
                {dropdownOptions.testingStandards.map((std) => (
                  <option key={std} value={std}>{std}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Hydrostatic Testing Parameters */}
          <div className="form-section">
            <h3>Hydrostatic Testing Parameters</h3>

            {/* Back Seat */}
            <div className="subsection">
              <h4>Back Seat</h4>
              <div className="form-row two-columns">
                <div className="form-group">
                  <label>Test Pressure (Kg/cm<sup>2</sup>)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="hydro_back_seat_pressure"
                    value={formData.hydro_back_seat_pressure}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Duration (seconds)</label>
                  <input
                    type="number"
                    name="hydro_back_seat_duration"
                    value={formData.hydro_back_seat_duration}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Acceptance Criteria</label>
                <input
                  type="text"
                  name="hydro_back_seat_acceptance"
                  value={formData.hydro_back_seat_acceptance}
                  onChange={handleInputChange}
                  placeholder="e.g., No Visible Leakage Found"
                />
              </div>
            </div>

            {/* Body */}
            <div className="subsection">
              <h4>Body</h4>
              <div className="form-row two-columns">
                <div className="form-group">
                  <label>Test Pressure (Kg/cm<sup>2</sup>) <span className="required">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    name="hydro_body_pressure"
                    value={formData.hydro_body_pressure}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Duration (seconds) <span className="required">*</span></label>
                  <input
                    type="number"
                    name="hydro_body_duration"
                    value={formData.hydro_body_duration}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Acceptance Criteria <span className="required">*</span></label>
                <input
                  type="text"
                  name="hydro_body_acceptance"
                  value={formData.hydro_body_acceptance}
                  onChange={handleInputChange}
                  placeholder="e.g., No Visible Leakage or Seepage"
                  required
                />
              </div>
            </div>

            {/* Seat */}
            <div className="subsection">
              <h4>Seat</h4>
              <div className="form-row two-columns">
                <div className="form-group">
                  <label>Test Pressure (Kg/cm<sup>2</sup>) <span className="required">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    name="hydro_seat_pressure"
                    value={formData.hydro_seat_pressure}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Duration (seconds) <span className="required">*</span></label>
                  <input
                    type="number"
                    name="hydro_seat_duration"
                    value={formData.hydro_seat_duration}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Acceptance Criteria <span className="required">*</span></label>
                <input
                  type="text"
                  name="hydro_seat_acceptance"
                  value={formData.hydro_seat_acceptance}
                  onChange={handleInputChange}
                  placeholder="e.g., No Visible Leakage Found"
                  required
                />
              </div>
            </div>
          </div>

          {/* Pneumatic Testing Parameters */}
          <div className="form-section">
            <h3>Pneumatic Testing Parameters</h3>

            {/* Seat */}
            <div className="subsection">
              <h4>Seat</h4>
              <div className="form-row two-columns">
                <div className="form-group">
                  <label>Test Pressure (Kg/cm<sup>2</sup>) <span className="required">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    name="pneumatic_seat_pressure"
                    value={formData.pneumatic_seat_pressure}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Duration (seconds) <span className="required">*</span></label>
                  <input
                    type="number"
                    name="pneumatic_seat_duration"
                    value={formData.pneumatic_seat_duration}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Acceptance Criteria <span className="required">*</span></label>
                <input
                  type="text"
                  name="pneumatic_seat_acceptance"
                  value={formData.pneumatic_seat_acceptance}
                  onChange={handleInputChange}
                  placeholder="e.g., Max 40 bubbles/min allowed"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Footer */}
        <div className="form-footer">
          <button type="button" className="btn-cancel" onClick={handleBackToList}>
            <span className="cancel-icon">&times;</span> Cancel
          </button>
          <div className="btn-group">
            {!editingId && (
              <button
                type="button"
                className="btn-save-another"
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading}
              >
                Save & Add Another
              </button>
            )}
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? "Saving..." : editingId ? "Update Mapping" : "Save Mapping"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <div className="testing-details-container">
      {viewMode === "list" ? renderListView() : renderFormView()}
    </div>
  );
}

export default TestingDetails;
